import { useState, useRef, useCallback } from 'preact/hooks';
import { createStartGuard } from './startGuard';

interface MediaRecorderErrorEvent extends Event {
  error?: DOMException;
}

export const SOFT_CAP_MS = 50 * 1000;  // 50s — warn before hard stop
export const HARD_CAP_MS = 60 * 1000;  // 1 min hard stop (Whisper OOM guard)
export const AUTO_STOP_SILENCE_MS = 3_000; // auto-stop 3s after the silence toast if still silent
export const LEVEL_BAR_COUNT = 12;

export interface RecorderOptions {
  /** Called once when recording reaches the soft cap (default 3 min) */
  onSoftCap?: () => void;
  /** Called when hard cap (5 min) auto-stops — provides chunks to caller */
  onHardCap?: (chunks: Blob[]) => void;
  /** Called on each 250ms MediaRecorder chunk as it arrives */
  onChunk?: (chunk: Blob) => void | Promise<void>;
}

export function useRecorder(options?: RecorderOptions) {
  const [recording, setRecording] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [levelBars, setLevelBars] = useState<number[]>(Array(LEVEL_BAR_COUNT).fill(0));
  /** UUID v4 minted at start() — stable for the lifetime of one recording session. */
  const [recordingId, setRecordingId] = useState<string | undefined>(undefined);

  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const softCapFiredRef = useRef(false);
  const hardCapFiredRef = useRef(false);
  const extCapRef = useRef(0);
  const stopResolveRef = useRef<((chunks: Blob[]) => void) | null>(null);
  const userStoppedRef = useRef(false);
  const visibilityCleanupRef = useRef<(() => void) | null>(null);
  // Single-flight latch (#1801): a second start() while the first getUserMedia
  // is still in flight would run a full second init and orphan the losing
  // stream/AudioContext. See startGuard.ts.
  const startGuardRef = useRef(createStartGuard());

  // Keep callbacks in refs so they never appear in useCallback dep arrays
  const onSoftCapRef = useRef(options?.onSoftCap);
  const onHardCapRef = useRef(options?.onHardCap);
  const onChunkRef = useRef(options?.onChunk);
  onSoftCapRef.current = options?.onSoftCap;
  onHardCapRef.current = options?.onHardCap;
  onChunkRef.current = options?.onChunk;

  /** One-time +60s extension to the soft cap */
  const extendCap = useCallback(() => {
    if (extCapRef.current === 0) {
      extCapRef.current = 60 * 1000;
      softCapFiredRef.current = false;
    }
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (visibilityCleanupRef.current) {
      visibilityCleanupRef.current();
      visibilityCleanupRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.onstatechange = null;
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
    }
  }, []);

  const start = useCallback(async (onError?: (msg: string) => void) => {
    // Re-entry guard (#1801): drop a second start() while the first
    // getUserMedia() is still in flight. begin() is synchronous — a concurrent
    // caller no-ops here, BEFORE the first await, so it never runs a second
    // getUserMedia/AudioContext/MediaRecorder init that would orphan the losing
    // stream (mic stays hot) and AudioContext (leaks). Cleared in finally.
    if (!startGuardRef.current.begin()) return;
    // Capture onError so async lifecycle handlers (track ended, pagehide,
    // recorder.onerror) can surface failures to the UI, not just the initial
    // getUserMedia rejection path.
    const reportError = (msg: string) => onError?.(msg);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });

      // Shared AudioContext — do NOT clone the stream; clone breaks echo cancellation.
      // createMediaStreamSource feeds both MediaRecorder and AnalyserNode from the same source.
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      // Mobile Chrome starts AudioContext suspended — must resume inside user gesture
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
      }
      const source = audioCtx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Prefer WebM/Opus (smallest size, universal Whisper support).
      // Fall through to audio/mp4 on devices where WebM is unavailable
      // (e.g. some Android OEM browsers, older Samsung Internet).
      // Omitting the mimeType entirely lets the browser choose its default —
      // only used as a last resort because the format may be unrecognized.
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : '';

      // 32 kbps Opus has effectively zero WER regression vs the 128 kbps
      // default for speech (Whisper's training distribution is well below
      // 32 kbps), but quarters wire size — a 2-min recording goes from
      // ~2 MB to ~480 KB, comfortably under the Socket.IO 20 MB cap.
      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32_000 });
      chunksRef.current = [];
      startTimeRef.current = Date.now();
      softCapFiredRef.current = false;
      hardCapFiredRef.current = false;
      extCapRef.current = 0;
      stopResolveRef.current = null;
      userStoppedRef.current = false;
      setDurationMs(0);
      setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));
      // Mint a fresh recordingId for this recording session.
      // crypto.randomUUID() is available in all modern browsers and in the
      // Web Crypto API (no import needed). Falls back to undefined on very old
      // clients — the backend gracefully treats absent recordingId as legacy path.
      const newRecordingId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : undefined;
      setRecordingId(newRecordingId);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          onChunkRef.current?.(e.data);
        }
      };

      recorder.onerror = (e: Event) => {
        const err = e as MediaRecorderErrorEvent;
        const name = err?.error?.name || 'unknown';
        console.error('[useRecorder] MediaRecorder error:', name, err?.error?.message);
        reportError(`Recording stopped: ${name}. Please try again.`);
        cleanup();
        // Treat like a hard-cap stop: deliver whatever we have
        if (recorder.state !== 'inactive') {
          try { recorder.stop(); } catch { /* already stopped */ }
        }
      };

      // Single onstop handler: resolves the stop() promise if one is pending,
      // otherwise invokes onHardCap (hard-cap auto-stop path).
      recorder.onstop = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const wasUserStopped = userStoppedRef.current;
        if (!wasUserStopped && !hardCapFiredRef.current) {
          console.warn(`[useRecorder] Unexpected stop after ${(elapsed / 1000).toFixed(1)}s with ${chunksRef.current.length} chunks`);
        }
        recorder.stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));
        const resolve = stopResolveRef.current;
        stopResolveRef.current = null;
        if (resolve) {
          resolve(chunksRef.current);
        } else {
          onHardCapRef.current?.(chunksRef.current);
        }
      };

      // Re-acquire AudioContext if browser suspends it mid-recording
      audioCtx.onstatechange = () => {
        if (audioCtx.state === 'suspended' && recorder.state === 'recording') {
          console.warn('[useRecorder] AudioContext suspended mid-recording, resuming');
          audioCtx.resume().catch(() => {});
        }
      };

      // Keep recording alive when page visibility changes (mobile tab switch / screen dim)
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible' && audioCtx.state === 'suspended') {
          console.warn('[useRecorder] Page visible again, resuming AudioContext');
          audioCtx.resume().catch(() => {});
        }
      };
      // pagehide fires on iOS Safari when the user switches apps or locks the
      // screen — at which point Safari kills MediaRecorder without notice. Stop
      // cleanly so the user-stopped path delivers the chunks we have, instead
      // of losing them to the silent kill.
      const onPageHide = () => {
        if (recorder.state === 'recording') {
          console.warn('[useRecorder] pagehide fired mid-recording — stopping cleanly');
          reportError('Recording paused — your audio so far was captured.');
          try { recorder.stop(); } catch { /* already stopped */ }
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pagehide', onPageHide);
      visibilityCleanupRef.current = () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
      };

      const freqData = new Uint8Array(analyser.frequencyBinCount);
      // Silence-detection state: when the mic goes dead mid-recording (mobile
      // browsers do this silently, no audioTrack.onmute event), the analyser
      // reads near-zero across all bins. After ~2s of continuous silence we
      // toast the user — they're typically not watching the level bars and
      // would otherwise hold PTT for 30 s and get a one-word transcript.
      // Forensic: a real prod recording (2026-04-30T06-07-35) had RMS drop
      // from −22 dB to −75 dB at the 5-second mark; whisper transcribed it
      // as "Tell me." (the only audible speech) and the user was confused.
      const SILENCE_THRESHOLD_BYTE = 6; // mean of all freq bins; speech ~30-80
      const SILENCE_DURATION_MS = 2000;
      let silenceMs = 0;
      let silenceWarningFired = false;
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setDurationMs(elapsed);

        // Level bars from frequency spectrum
        analyser.getByteFrequencyData(freqData);
        const binsPerBar = Math.floor(freqData.length / LEVEL_BAR_COUNT);
        const bars = Array.from({ length: LEVEL_BAR_COUNT }, (_, i) => {
          let sum = 0;
          for (let j = i * binsPerBar; j < (i + 1) * binsPerBar; j++) sum += freqData[j];
          return Math.round((sum / binsPerBar / 255) * 100);
        });
        setLevelBars(bars);

        // Mean level across all bins for silence detection. Track contiguous
        // silent ticks; reset on any audible tick. Fire once per recording.
        let meanLevel = 0;
        for (let i = 0; i < freqData.length; i++) meanLevel += freqData[i];
        meanLevel = meanLevel / freqData.length;
        if (meanLevel < SILENCE_THRESHOLD_BYTE) {
          silenceMs += 100;
        } else {
          silenceMs = 0;
        }
        if (!silenceWarningFired && silenceMs >= SILENCE_DURATION_MS && elapsed > SILENCE_DURATION_MS) {
          silenceWarningFired = true;
          console.warn(
            `[useRecorder] Mic silent for ${silenceMs}ms ` +
            `(mean=${meanLevel.toFixed(1)}, elapsed=${elapsed}ms) — surfacing toast`,
          );
          reportError(
            'Microphone went silent — check mic permission / Bluetooth routing and try again.',
          );
        }
        // Auto-stop if silence persists past the toast window. Delivers whatever
        // was captured before the mic went dead rather than holding PTT for 60s.
        if (silenceWarningFired && silenceMs >= SILENCE_DURATION_MS + AUTO_STOP_SILENCE_MS) {
          cleanup();
          if (recorder.state !== 'inactive') recorder.stop();
        }

        // Soft cap: extendable once by 60s
        const softThreshold = SOFT_CAP_MS + extCapRef.current;
        if (!softCapFiredRef.current && elapsed >= softThreshold) {
          softCapFiredRef.current = true;
          onSoftCapRef.current?.();
        }

        // Hard cap: auto-stop, chunks delivered via onHardCap in onstop
        if (!hardCapFiredRef.current && elapsed >= HARD_CAP_MS) {
          hardCapFiredRef.current = true;
          cleanup();
          if (recorder.state !== 'inactive') recorder.stop();
        }
      }, 100);

      // Detect if the browser kills the audio track mid-recording
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          if (recorder.state === 'recording') {
            console.warn('[useRecorder] Audio track ended unexpectedly — browser killed the stream');
            reportError('Microphone disconnected mid-recording. Please try again.');
            cleanup();
            try { recorder.stop(); } catch { /* already stopped */ }
          }
        };
        audioTrack.onmute = () => {
          console.warn('[useRecorder] Audio track muted by browser');
        };
        audioTrack.onunmute = () => {
          console.warn('[useRecorder] Audio track unmuted');
        };
      }

      recorder.start(250);
      mediaRef.current = recorder;
      setRecording(true);
    } catch (err) {
      console.error('Mic access denied:', err);
      onError?.('Microphone access denied — allow it in browser settings');
    } finally {
      // Init window closed (success tail OR getUserMedia reject) — release the
      // latch so a normal retry / next recording can start.
      startGuardRef.current.end();
    }
  }, [cleanup]);

  const stop = useCallback((): Promise<Blob[]> => {
    return new Promise((resolve) => {
      userStoppedRef.current = true;
      cleanup();
      const recorder = mediaRef.current;
      if (!recorder || recorder.state === 'inactive') {
        setRecording(false);
        setLevelBars(Array(LEVEL_BAR_COUNT).fill(0));
        resolve(chunksRef.current);
        return;
      }
      stopResolveRef.current = resolve;
      recorder.stop();
    });
  }, [cleanup]);

  return { recording, durationMs, levelBars, extendCap, start, stop, recordingId };
}
